import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import _get from 'lodash/get';
import ShopifyOrderDetailModal from 'components/v2/shopify-order-detail-modal/shopify-order-detail-modal';
import NumberedPin from 'components/v2/common/numbered-pin';
import ShopifyTrackEventContext from 'views/v2/shopify-home/track-event';
import {
  SCRIBE_STATE_NAMES,
  SCRIBE_SHOPIFY_EVENTS,
  SCRIBE_SHOPIFY_PARAMS,
} from 'applications/scribe/instrumentations/track';
import I18n from 'i18n-js';
import RecipientMSDIcon from 'public/icons/recipient-msd.svg';
import RecipientSingleIcon from 'public/icons/recipient-single.svg';
import { MSD_STEP_STATE } from 'constants/order-constants';

import { ColumnWithIcon, ColumnWithPopover, InfoCard } from './common';
import styles from './delivery-step.less';

export const getFirstStepToDisplay = steps => {
  const firstOngoingStep = steps.find(s => s.status === MSD_STEP_STATE.Ongoing);
  return firstOngoingStep || steps[0];
};

const onClickOrderDetail = (ctx, shopifyMetaData, contact) => {
  ctx.fireShopifyTrackEvent({
    stateName: SCRIBE_STATE_NAMES.EXPRESS_BOOKINGS_WEB,
    eventName: SCRIBE_SHOPIFY_EVENTS.SHOPIFY_ORDER_NUMBER,
    facets: {
      [SCRIBE_SHOPIFY_PARAMS.SHOPIFY_ORDER_ID]: _get(shopifyMetaData, 'ID', ''),
      [SCRIBE_SHOPIFY_PARAMS.TOTAL_ITEMS]: _get(
        shopifyMetaData,
        'lineItems.length',
        0,
      ),
    },
  });
  return ShopifyOrderDetailModal({ ctx, shopifyMetaData, contact });
};

const renderOrderDetailLink = (ctx, shopifyMetaData, contact, index) => (
  <a
    key={index}
    role="button"
    tabIndex="0"
    onClick={() => onClickOrderDetail(ctx, shopifyMetaData, contact)}
    onKeyDown={null}
  >
    {!index ? 'Shopify Order ' : ', '} {_get(shopifyMetaData, 'name', '')}
  </a>
);

const renderMultipleOrderDetailLink = (ctx, shopifyMetaDatas, contact) => (
  <h6 className={styles.shopifyOrderLink}>
    {shopifyMetaDatas.map((order, index) =>
      renderOrderDetailLink(ctx, order, contact, index),
    )}
  </h6>
);

export const DeliveryStep = ({ step, shopifyMetaDatas }) => {
  const { contact, location } = step;
  const trackCtx = React.useContext(ShopifyTrackEventContext);

  return (
    <InfoCard
      primary={_get(contact, 'name', '')}
      secondary={_get(contact, 'phone')}
      tertiary={_get(location, 'address')}
      more={
        shopifyMetaDatas && shopifyMetaDatas.length
          ? renderMultipleOrderDetailLink(trackCtx, shopifyMetaDatas, contact)
          : null
      }
    />
  );
};

DeliveryStep.propTypes = {
  step: PropTypes.shape({}),
  shopifyMetaDatas: PropTypes.arrayOf(PropTypes.shape({})),
};

DeliveryStep.defaultProps = {
  step: {},
  shopifyMetaDatas: [],
};

const DeliveryStepWithPopup = ({ isPickUp, steps, shopifyMetaDatas }) => {
  const trackCtx = React.useContext(ShopifyTrackEventContext);

  if (!steps || steps.length === 0) {
    return null;
  }

  const firstOngoingStep = useMemo(() => getFirstStepToDisplay(steps), [steps]);

  const firstShopifyMetaData = shopifyMetaDatas.filter(
    shopify => shopify.step_idx === 1,
  );

  const popoverContent = useMemo(
    () =>
      steps.length === 1 ? (
        <DeliveryStep
          step={firstOngoingStep}
          shopifyMetaDatas={firstShopifyMetaData}
        />
      ) : (
        steps.map((step, index) => (
          <div key={`${index + 1}`} className={styles.stepWithPin}>
            <div className={styles.pinWithDotLine}>
              <NumberedPin
                className={styles.icon}
                showNumber
                isSender={false}
                number={`${index + 1}`}
              />
              <div className={styles.dotLine} />
            </div>
            <div className={styles.stepWrapper}>
              <DeliveryStep
                step={step}
                shopifyMetaDatas={shopifyMetaDatas.filter(
                  shopify => shopify.step_idx === index + 1,
                )}
              />
            </div>
          </div>
        ))
      ),
    [steps, firstOngoingStep, firstShopifyMetaData, shopifyMetaDatas],
  );

  const icon = useMemo(
    () =>
      isPickUp
        ? undefined
        : steps.length > 1 ? RecipientMSDIcon : RecipientSingleIcon,
    [isPickUp, steps],
  );

  const firstContent = useMemo(
    () => {
      const firstContact = _get(firstOngoingStep, 'contact');
      return (
        <ColumnWithIcon iconSrc={icon}>
          <div>
            <div>
              {_get(firstContact, 'name')}{' '}
              {steps.length > 1
                ? I18n.t('v2.bookingsPage.andMore', { amt: steps.length - 1 })
                : ''}
            </div>
            {firstShopifyMetaData && firstShopifyMetaData.length
              ? renderMultipleOrderDetailLink(
                  trackCtx,
                  firstShopifyMetaData,
                  firstContact,
                )
              : null}
          </div>
        </ColumnWithIcon>
      );
    },
    [steps, firstOngoingStep, firstShopifyMetaData, trackCtx, icon],
  );

  return (
    <ColumnWithPopover popoverContent={popoverContent}>
      <div>{firstContent}</div>
    </ColumnWithPopover>
  );
};

DeliveryStepWithPopup.propTypes = {
  isPickUp: PropTypes.bool,
  steps: PropTypes.arrayOf(PropTypes.shape({})),
  shopifyMetaDatas: PropTypes.arrayOf(PropTypes.shape({})),
};

DeliveryStepWithPopup.defaultProps = {
  isPickUp: false,
  steps: [],
  shopifyMetaDatas: [],
};

export default DeliveryStepWithPopup;
